import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { api, fmtMoney, formatApiError } from "@/lib/api";
import { Pencil, History, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Convert ISO datetime to <input type="datetime-local"> value (yyyy-MM-ddTHH:mm)
function isoToLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function localToIso(local) {
  if (!local) return "";
  try {
    const d = new Date(local);
    if (isNaN(d.getTime())) return "";
    return d.toISOString();
  } catch {
    return "";
  }
}

function fmtClock(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const FIELD_LABEL = {
  clock_in: "Clock In",
  clock_out: "Clock Out",
  lunch_start: "Lunch Start",
  lunch_end: "Lunch End",
};

export default function AdminTimeCard({ submission, timeEntry, lunchEntry, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const initialPunches = () =>
    ((timeEntry?.punches || []).length > 0
      ? (timeEntry?.punches || [])
      : (timeEntry?.clock_in ? [{ id: null, clock_in: timeEntry.clock_in, clock_out: timeEntry.clock_out || null }] : [])
    ).map((p) => ({
      id: p.id || null,
      clock_in: isoToLocal(p.clock_in),
      clock_out: isoToLocal(p.clock_out),
    }));
  const [form, setForm] = useState({
    punches: initialPunches(),
    lunch_start: isoToLocal(lunchEntry?.lunch_start),
    lunch_end: isoToLocal(lunchEntry?.lunch_end),
    reason: "",
  });

  React.useEffect(() => {
    setForm({
      punches: initialPunches(),
      lunch_start: isoToLocal(lunchEntry?.lunch_start),
      lunch_end: isoToLocal(lunchEntry?.lunch_end),
      reason: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeEntry, lunchEntry?.lunch_start, lunchEntry?.lunch_end, open]);

  const submit = async () => {
    if (!form.reason.trim()) {
      toast.error("Please add a reason for the edit (required for audit log).");
      return;
    }
    // Validate every punch has at least a clock-in, and clock_out >= clock_in
    for (let i = 0; i < form.punches.length; i++) {
      const p = form.punches[i];
      if (!p.clock_in) {
        toast.error(`Punch #${i + 1} needs a Clock In time.`);
        return;
      }
      if (p.clock_out && new Date(p.clock_out) <= new Date(p.clock_in)) {
        toast.error(`Punch #${i + 1}: Clock Out must be after Clock In.`);
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        user_id: submission.user_id,
        date: submission.date,
        reason: form.reason.trim(),
        punches: form.punches.map((p) => ({
          id: p.id || undefined,
          clock_in: localToIso(p.clock_in),
          clock_out: p.clock_out ? localToIso(p.clock_out) : null,
        })),
      };
      // Lunch — only include if changed
      const origLunch = {
        lunch_start: lunchEntry?.lunch_start || "",
        lunch_end: lunchEntry?.lunch_end || "",
      };
      for (const f of ["lunch_start", "lunch_end"]) {
        const newIso = form[f] ? localToIso(form[f]) : "";
        if (newIso !== origLunch[f]) {
          payload[f] = newIso || "";
        }
      }
      const { data } = await api.post("/time/admin-edit", payload);
      if (!data.changed) {
        toast.message("No changes detected");
      } else {
        toast.success("Time updated");
        onRefresh?.();
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const addPunch = () => setForm((f) => ({ ...f, punches: [...f.punches, { id: null, clock_in: "", clock_out: "" }] }));
  const removePunch = (idx) => setForm((f) => ({ ...f, punches: f.punches.filter((_, i) => i !== idx) }));
  const updatePunch = (idx, key, value) => setForm((f) => ({
    ...f,
    punches: f.punches.map((p, i) => i === idx ? { ...p, [key]: value } : p),
  }));

  const history = [
    ...((timeEntry?.edit_history) || []),
    ...((lunchEntry?.edit_history) || []),
  ].sort((a, b) => (b.edited_at || "").localeCompare(a.edited_at || ""));

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="admin-time-card">
      <CardContent className="p-5 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold font-display">Time</h3>
          <div className="flex items-center gap-1">
            {history.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)} className="text-xs" data-testid="admin-time-history-btn">
                <History className="h-3.5 w-3.5 mr-1" /> Edits ({history.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="admin-time-edit-btn">
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
            </Button>
          </div>
        </div>
        <TimeRow label="Clock In" value={fmtClock(timeEntry?.clock_in)} edited={hasEdit(timeEntry?.edit_history, "clock_in")} />
        <TimeRow label="Lunch Start" value={fmtClock(lunchEntry?.lunch_start)} edited={hasEdit(lunchEntry?.edit_history, "lunch_start")} />
        <TimeRow label="Lunch End" value={fmtClock(lunchEntry?.lunch_end)} edited={hasEdit(lunchEntry?.edit_history, "lunch_end")} />
        <TimeRow label="Clock Out" value={fmtClock(timeEntry?.clock_out)} edited={hasEdit(timeEntry?.edit_history, "clock_out")} />
        {((timeEntry?.punches || []).length > 1 || (timeEntry?.punches || []).some(p => p?.force_closed)) && (
          <div className="mt-2 pt-2 border-t border-slate-100" data-testid="admin-punches-list">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Punches ({(timeEntry?.punches || []).length})</p>
            <div className="space-y-1">
              {timeEntry.punches.map((p, idx) => (
                <div key={p.id || idx} className="flex items-center justify-between text-xs text-slate-700" data-testid={`admin-punch-row-${idx}`}>
                  <span className="text-slate-500">#{idx + 1}</span>
                  <span className="text-right">
                    {fmtClock(p.clock_in)} → {p.clock_out ? fmtClock(p.clock_out) : <span className="text-green-600 font-semibold">in progress</span>}
                    {p.force_closed && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase">Auto-closed</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(submission.hours?.flagged_punches || []).length > 0 && (
          <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="flagged-punches-warning">
            <p className="font-semibold">⚠️ Flagged punches</p>
            <p className="mt-0.5">{submission.hours.flagged_punches.length} punch{submission.hours.flagged_punches.length === 1 ? "" : "es"} exceeded the 16-hour safety cap and were capped automatically. Please review and adjust the time entries above.</p>
          </div>
        )}
        <div className="border-t border-slate-100 pt-2 mt-1">
          <TimeRow label="Total Hours" value={`${(submission.hours?.total_hours || 0).toFixed(2)}h`} />
          <TimeRow label="Net Hours" value={`${(submission.hours?.net_hours || 0).toFixed(2)}h`} bold />
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="admin-time-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Time — {submission.date}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Edit any punch's clock-in or clock-out. Add a new punch for split shifts. Remove a punch if it shouldn't exist. Leave Clock Out blank to mark a punch as still open.</p>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Punches</Label>
                <Button type="button" variant="outline" size="sm" onClick={addPunch} data-testid="add-punch-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add punch
                </Button>
              </div>
              {form.punches.length === 0 && (
                <p className="text-sm text-slate-500 italic px-2 py-3 border border-dashed border-slate-200 rounded">No punches. Click "Add punch" to create one.</p>
              )}
              <div className="space-y-3">
                {form.punches.map((p, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50" data-testid={`edit-punch-row-${idx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-700">Punch #{idx + 1}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removePunch(idx)} className="h-6 px-2 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`remove-punch-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <DTField
                        label="Clock In"
                        value={p.clock_in}
                        onChange={(v) => updatePunch(idx, "clock_in", v)}
                        testId={`edit-punch-${idx}-clock-in`}
                      />
                      <DTField
                        label="Clock Out"
                        value={p.clock_out}
                        onChange={(v) => updatePunch(idx, "clock_out", v)}
                        testId={`edit-punch-${idx}-clock-out`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-2 block">Lunch (optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <DTField label="Lunch Start" value={form.lunch_start} onChange={(v) => setForm({ ...form, lunch_start: v })} testId="edit-lunch-start" />
                <DTField label="Lunch End" value={form.lunch_end} onChange={(v) => setForm({ ...form, lunch_end: v })} testId="edit-lunch-end" />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Reason <span className="text-red-600">*</span></Label>
              <Textarea
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={2}
                placeholder="e.g., Employee forgot to clock out — confirmed end time with phone call."
                className="mt-1"
                data-testid="edit-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="admin-time-save-btn">
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="admin-time-history-dialog">
          <DialogHeader>
            <DialogTitle>Edit History — {submission.date}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {history.length === 0 && <p className="text-sm text-slate-500">No edits.</p>}
            {history.map((h, idx) => (
              <div key={idx} className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`edit-history-row-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900">{FIELD_LABEL[h.field] || h.field}</span>
                  <span className="text-xs text-slate-500">{fmtClock(h.edited_at)}</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  <span className="text-red-600 line-through">{fmtClock(h.previous) || "—"}</span>
                  {" → "}
                  <span className="text-green-700 font-semibold">{fmtClock(h.new) || "(cleared)"}</span>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  by <span className="font-semibold">{h.edited_by_name}</span>
                  {h.reason ? <> — {h.reason}</> : null}
                </p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function DTField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</Label>
      <div className="flex gap-2 mt-1">
        <Input
          type="datetime-local"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
          data-testid={testId}
        />
        {value && (
          <Button type="button" variant="outline" size="sm" onClick={() => onChange("")} title="Clear" data-testid={`${testId}-clear`}>
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

function TimeRow({ label, value, bold, edited }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">
        {label}
        {edited && (
          <span className="ml-1 inline-block px-1 py-0 rounded text-[8px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 align-middle">
            Edited
          </span>
        )}
      </span>
      <span className={bold ? "font-bold text-slate-900" : "text-slate-900"}>{value}</span>
    </div>
  );
}

function hasEdit(history, field) {
  return (history || []).some((h) => h.field === field);
}
